import { Accordion } from '@delmonicos/shared-design-system-react/src/components/accordion/accordion';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import {
  Icon,
  IconColor,
  IconSize,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import { MiniTitle } from '@delmonicos/shared-design-system-react/src/components/typography/mini-title/mini-title';
import {
  Text,
  TextColor,
  TextFontSize,
  TextWeight,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useChargePointContext } from '@app/feature/charge-point/charge-point.context';

import * as css from 'src/feature/charge-point/charge-point-details/charge-point-details.module.scss';

export const InformationCardComponent = (): React.JSX.Element => {
  const { t } = useTranslation();

  const chargePoint = useChargePointContext();
  const generalInformation = chargePoint.channels.at(0);

  const renderAdditionalInformationTitle = () => (
    <Stack gap={GapEnum.XS} alignItems="center">
      <Icon name="electric-terminal" iconSize={IconSize.Small} />
      <Text fontSize={TextFontSize.Large} fontWeight={TextWeight.SemiBold}>
        {t('$.charge-point-details.information.additional-information')}
      </Text>
    </Stack>
  );

  return (
    <Card
      className={classNames([css.generalCard])}
      direction="row"
      flex={1}
      gap={GapEnum.L}>
      <Stack direction="column" gap={GapEnum.M} flex={1}>
        <Card flex={1}>
          <Card.RemoveMargin
            removeTop
            removeBottom
            removeLeft
            removeRight
            className={css.chargePointImage}
            style={{
              backgroundImage: `url(${
                chargePoint.previewImageUrl ??
                'https://images.unsplash.com/photo-1635451048957-3beb7175f57f?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
              })`,
            }}
          />
        </Card>
        <Stack alignItems="center" justifyContent="space-between">
          <Stack alignItems="center" gap={GapEnum.XS} flex={1}>
            <Icon
              iconSize={IconSize.Small}
              color={IconColor.Secondary}
              name="terminal"
            />
            <MiniTitle color={TextColor.Secondary}>
              {t('delmonicos-shared-sdk.outputs-charge-point.model')}
            </MiniTitle>
          </Stack>
          <Stack flex={1}>
            <Text fontSize={TextFontSize.Large}>
              {chargePoint.model === 'unknown' ? '-' : chargePoint.model}
            </Text>
          </Stack>
        </Stack>
        <Stack alignItems="center" justifyContent="space-between">
          <Stack alignItems="center" gap={GapEnum.XS} flex={1}>
            <Icon
              color={IconColor.Secondary}
              iconSize={IconSize.Small}
              name="terminal"
            />
            <MiniTitle color={TextColor.Secondary}>
              {t('delmonicos-shared-sdk.outputs-charge-point.serial-number')}
            </MiniTitle>
          </Stack>
          <Stack flex={1}>
            <Text fontSize={TextFontSize.Large}>
              {chargePoint.serialNumber === 'unknown' ||
              chargePoint.serialNumber === undefined
                ? '-'
                : chargePoint.serialNumber}
            </Text>
          </Stack>
        </Stack>
        <Stack alignItems="center" justifyContent="space-between">
          <Stack alignItems="center" gap={GapEnum.XS} flex={1}>
            <Icon
              color={IconColor.Secondary}
              iconSize={IconSize.Small}
              name="building-02"
            />
            <MiniTitle color={TextColor.Secondary}>
              {t('delmonicos-shared-sdk.outputs-charge-point.vendor')}
            </MiniTitle>
          </Stack>
          <Stack flex={1}>
            <Text fontSize={TextFontSize.Large}>
              {chargePoint.vendor === 'unknown' ||
              chargePoint.vendor === undefined
                ? '-'
                : chargePoint.vendor}
            </Text>
          </Stack>
        </Stack>
        <Divider />
        <Accordion isOpen={false} title={renderAdditionalInformationTitle()}>
          <Stack direction="column" gap={GapEnum.M}>
            <Stack direction="column" gap={GapEnum.XS}>
              <Stack alignItems="center" gap={GapEnum.XS}>
                <Icon
                  color={IconColor.Secondary}
                  iconSize={IconSize.Small}
                  name="terminal"
                />
                <MiniTitle color={TextColor.Secondary}>
                  {t('delmonicos-shared-sdk.outputs-channel.capabilities')}
                </MiniTitle>
              </Stack>
              {generalInformation?.capabilities &&
              generalInformation?.capabilities.length !== 0 ? (
                <div className={css.tagContainer}>
                  {generalInformation?.capabilities.map((capability) => (
                    <Tag
                      key={capability}
                      className="bg-surface-brand-decorative-bis">
                      <Text fontSize={TextFontSize.Small} italic>
                        {t(
                          `delmonicos-shared-sdk.ocpi-protocol-enums.evse-capability.${capability}`,
                        )}
                      </Text>
                    </Tag>
                  ))}
                </div>
              ) : (
                <Tag className="bg-surface-secondary" flex={1}>
                  <Text fontSize={TextFontSize.Small}>-</Text>
                </Tag>
              )}
            </Stack>
            <Stack direction="column" gap={GapEnum.XS}>
              <Stack alignItems="center" gap={GapEnum.XS}>
                <Icon
                  color={IconColor.Secondary}
                  iconSize={IconSize.Small}
                  name="parking"
                />
                <MiniTitle color={TextColor.Secondary}>
                  {t(
                    'delmonicos-shared-sdk.outputs-channel.parking-restrictions',
                  )}
                </MiniTitle>
              </Stack>
              {generalInformation?.parkingRestrictions &&
              generalInformation?.parkingRestrictions.length !== 0 ? (
                <div className={css.tagContainer}>
                  {generalInformation?.parkingRestrictions.map(
                    (restriction) => (
                      <Tag
                        key={restriction}
                        className="bg-surface-brand-decorative">
                        <Text fontSize={TextFontSize.Small} italic truncate>
                          {t(
                            `delmonicos-shared-sdk.ocpi-protocol-enums.parking-restriction.${restriction}`,
                          )}
                        </Text>
                      </Tag>
                    ),
                  )}
                </div>
              ) : (
                <Tag className="bg-surface-secondary" flex={1}>
                  <Text fontSize={TextFontSize.Small}>-</Text>
                </Tag>
              )}
            </Stack>
          </Stack>
        </Accordion>
      </Stack>
    </Card>
  );
};
